/* eslint-disable no-console */
import { useContext } from 'react';
import {
  A,
  ALPHANUMERIC_REGEX,
  DEFAULT_TIMEZONE,
  distanceMapper,
  HMS_FORMAT,
  INVERTED_DATE_FORMAT,
  KM,
  KW,
  KWH,
  MA,
  MOMENT_DAY,
  MOMENT_MILLISECOND,
  MOMENT_MINUTE,
  MS,
  MV,
  MW,
  MWH,
  NO_DATA, S,
  V,
  VALID_TIMEZONES,
  VALID_UNITS,
  W,
  WH,
} from 'consts';
import { Context } from 'context/Context';
import convert from 'convert-units';
import { camelCase, upperFirst } from 'lodash';
import moment from 'moment-timezone';
import { testRegex } from 'utils';

import 'moment/locale/it';
import 'moment/locale/es';
import 'moment-duration-format';

import useValidator from './useValidator';

// Modifica il formato dei nomi dei mesi completi
moment.updateLocale('it', {
  months(momentToFormat) {
    const months = [
      'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
    ];
    return months[momentToFormat.month()];
  },
  monthsShort(momentToFormat) {
    const monthsShort = [
      'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
      'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic',
    ];
    return monthsShort[momentToFormat.month()];
  },
});

const { isValidNumber } = useValidator();

moment.tz.setDefault(DEFAULT_TIMEZONE);

// EPOCH
const isTZvalid = (v, ...args) => v && moment(v, ...args)?.isValid();

const formatHMS = (
  value,
  durationType,
) => {
  let formattedValue = NO_DATA;
  if (isValidNumber(value)) {
    const duration = moment.duration(value, durationType);
    if (isTZvalid(duration)) {
      formattedValue = duration.format(HMS_FORMAT, { trim: false });
    }
  }
  return formattedValue;
};

const msToHs = (
  ms,
) => formatHMS(ms, MOMENT_MILLISECOND);

const minToHs = (
  min,
) => formatHMS(min, MOMENT_MINUTE);

const createDateFromString = (v) => (isTZvalid(v) ? moment(v).toDate() : null);

const toISO = (v) => (isTZvalid(v) ? moment(v).toISOString() : NO_DATA);

// UNIT
const formatValueNumber = (toFormat, from, to) => {
  if (!isValidNumber(toFormat)) {
    console.warn('Invalid value parameter', toFormat);
    return 0;
  }

  const base = distanceMapper[from] ?? from;
  const target = distanceMapper[to] ?? to;

  if (VALID_UNITS.indexOf(base) === -1) {
    console.warn(`Invalid base parameter passed to convert units [${base}]. pass one of ${VALID_UNITS}`);
    return 0;
  }
  if (VALID_UNITS.indexOf(target) === -1) {
    console.warn(`Invalid target parameter passed to convert units [${target}]. pass one of ${VALID_UNITS}`);
    return 0;
  }

  return convert(Number(toFormat)).from(base).to(target);
};

const formatPercentage = (toFormat) => (isValidNumber(toFormat) ? `${toFormat} %` : NO_DATA);

// NUMBER
const addZeroBefore = (v, toFixed = 2) => (isValidNumber(v) ? v.toString().padStart(toFixed, '0') : '');

const onlyNumber = (e) => { // TODO USE NUMBERINPUT WHEN THESE ARE USED
  if (['.', ',', 'e', '+', NO_DATA]
    .includes(e.key) && e.key.length === 1) { e.preventDefault(); }
};

const onlyNumberWithDecimal = (e) => { // TODO USE NUMBERINPUT WHEN THESE ARE USED
  if (['e', '+', NO_DATA]
    .includes(e.key) && e.key.length === 1) { e.preventDefault(); }
};

export default function useFormatter() {
  const {
    user: { timeZone, isKiloBase },
    localization: {
      countryFormatDate,
      timeFormat,
      numbersFormat,
      currency: userCurrency,
      countryID,
    },
  } = useContext(Context);

  // UTILS

  const numberInstance = (config = {}, format = numbersFormat) => new Intl.NumberFormat(format, config);

  const formatterNumber = (decimals = 2) => numberInstance({
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });

  const formatterCurrency = (iso = false, decimals = 2) => numberInstance({
    style: 'currency',
    currency: userCurrency,
    currencyDisplay: iso ? 'code' : 'symbol',
    maximumFractionDigits: decimals,
  });

  // UNIT

  const formatValue = (v, from, to, decimals) => {
    let value = v;
    if (!isValidNumber(value)) { value = 0; }
    return formatterNumber(decimals).format(formatValueNumber(value, from, to));
  };

  const formatFromTo = (v, from, to, decimals) => (isValidNumber(v)
    ? `${formatValue(v, from, to, decimals)} ${to}`
    : NO_DATA);

  const formatPower = (v, from = W, to = isKiloBase ? KW : MW) => formatFromTo(v, from, to);

  const formatElectricEnergy = (v, from = WH, decimals, to = isKiloBase ? KWH : MWH) => formatFromTo(v, from, to, decimals);

  const formatA = (v, from = MA, to = A) => formatFromTo(v, from, to);

  const formatV = (v, from = MV, to = V) => formatFromTo(v, from, to);

  const transformDistance = (v, from = KM, to = KM) => formatValue(v, from, to);

  const unit = {
    formatA,
    formatElectricEnergy,
    formatFromTo,
    formatPercentage,
    formatPower,
    formatterNumber,
    formatV,
    formatValueNumber,
    transformDistance,
  };

  // EPOCH
  const currentTimeZone = VALID_TIMEZONES.indexOf(timeZone) !== -1 ? timeZone : DEFAULT_TIMEZONE;

  const offsetDiff = () => {
    const browserTimeZone = moment.tz.guess();
    const browserOffset = moment.tz(browserTimeZone).utcOffset();
    const userOffset = moment.tz(currentTimeZone).utcOffset();
    return browserOffset - userOffset;
  };

  const toCustomFormat = (v, format) => (isTZvalid(v) ? moment.tz(v, currentTimeZone).format(format) : NO_DATA);

  const formatTime = (v) => toCustomFormat(v, timeFormat);

  const compareDate = (firstDate, secondDate) => {
    if (!isTZvalid(firstDate) || !isTZvalid(secondDate)) throw new Error();
    const dateOne = moment(firstDate);
    const dateTwo = moment(secondDate);
    if (dateOne.isSame(dateTwo)) return 0;
    if (dateOne.isAfter(dateTwo)) return -1;
    return 1;
  };

  const createNewDate = () => moment.tz(currentTimeZone).startOf(MOMENT_DAY).utc();

  const formatDate = (v) => (isTZvalid(v) ? toCustomFormat(v, countryFormatDate) : NO_DATA);

  const formatDateInverted = (v) => (isTZvalid(v) ? toCustomFormat(v, INVERTED_DATE_FORMAT) : NO_DATA); // data format for HTTPM post call - (create coupon)

  const formatDateTime = (v) => (isTZvalid(v) ? toCustomFormat(v, `${countryFormatDate} - ${timeFormat}`) : NO_DATA);

  // use this to populate datetimepicker with external data
  const inputTimeZoneHandler = (v) => {
    if (!isTZvalid(v)) return null;
    if (!currentTimeZone) return v;
    return new Date(moment(v).add(-offsetDiff(), MOMENT_MINUTE));
  };

  const timeStampSecondsConverter = (v) => (isTZvalid(v)
    ? moment.utc(formatValueNumber(v, S, MS)).local()?.format(`${countryFormatDate} - ${timeFormat}`) : NO_DATA);

  // use this before api calls
  const timeZoneHandler = (v) => {
    if (!isTZvalid(v)) return null;
    if (!currentTimeZone) return v;
    return new Date(moment(v).add(offsetDiff(), MOMENT_MINUTE));
  };

  const toMilliseconds = (v) => (isTZvalid(v) ? moment.tz(v, currentTimeZone)?.valueOf() : null);

  const toSeconds = (v) => (isTZvalid(v) ? Math.round(formatValueNumber(v, MS, S)) : null);

  const isBetween = (value, start, end, format) => moment(value, format).isBetween(
    moment(start, format), moment(end, format),
  );

  const toUnix = (v) => (isTZvalid(v) ? moment(v).unix() : null);

  const epoch = {
    compareDate,
    countryFormatDate,
    createDateFromString,
    createNewDate,
    currentTimeZone,
    formatDate,
    formatDateInverted,
    formatDateTime,
    formatTime,
    inputTimeZoneHandler,
    msToHs,
    minToHs,
    moment,
    timeStampSecondsConverter,
    timeZoneHandler,
    toCustomFormat,
    toISO,
    toMilliseconds,
    toSeconds,
    toUnix,
    isTZvalid,
    isBetween,
  };

  // CURRENCY
  const formatCurrency = (v, decimals = 0, iso = false) => {
    if (!isValidNumber(v)) return NO_DATA;
    const value = Number(v);
    if (countryID === 'CL') {
      return `$ ${formatterNumber().format(Number(value.toFixed(2)))}`;
    }
    return formatterCurrency(iso, decimals).format(value).toLocaleString('it');
  };

  const currency = {
    formatCurrency,
  };

  // NUMBER
  const formatDecimal = (v, decimals = 2) => (isValidNumber(v) ? formatterNumber(decimals).format(v) : null);
  const fixedDecimal = (v, decimals = 0) => (isValidNumber(v) ? v.toFixed(decimals) : null);

  const roundValue = (value) => (isValidNumber(value)
    ? Math.floor(value) : undefined);

  const number = {
    addZeroBefore,
    fixedDecimal,
    formatDecimal,
    onlyNumber,
    onlyNumberWithDecimal,
    roundValue,
  };

  // STRING

  const onlyAlphanumeric = (e) => {
    if (!testRegex(ALPHANUMERIC_REGEX, e.key) && e.key.length === 1) e.preventDefault();
  };

  const string = {
    capitalize: upperFirst,
    onlyAlphanumeric,
    toCamelCase: camelCase,
  };

  return ({
    currency,
    epoch,
    number,
    string,
    unit,
  });
}
